import { ChevronLeft, ChevronRight } from "lucide-react";
import * as React from "react";
import { DayPicker, useDayPicker, type MonthCaptionProps } from "react-day-picker";

import { buttonVariants } from "@/shared/components/ui/button";
import { cn } from "@/shared/libs/utils";
import { MonthYearDropdown } from "./dropdown-calendar";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function CustomMonthCaption({ calendarMonth }: MonthCaptionProps) {
    const { goToMonth, previousMonth, nextMonth } = useDayPicker();
    const month = calendarMonth.date;

    return (
        <div className="grid grid-cols-[auto_1fr_auto] items-center gap-2 pb-2">
            {/* 이전 달 */}
            <button
                type="button"
                onClick={() => previousMonth && goToMonth(previousMonth)}
                disabled={!previousMonth}
                className={cn(buttonVariants({ variant: "outline" }), "h-9 w-9 p-0 shrink-0")}
                aria-label="Previous month"
            >
                <ChevronLeft className="h-4 w-4 mx-auto" />
            </button>

            {/* 월/년도 드롭다운 */}
            <div className="flex items-center justify-center">
                <MonthYearDropdown month={month} onChange={goToMonth} />
            </div>

            {/* 다음 달 */}
            <button
                type="button"
                onClick={() => nextMonth && goToMonth(nextMonth)}
                disabled={!nextMonth}
                className={cn(buttonVariants({ variant: "outline" }), "h-9 w-9 p-0 shrink-0")}
                aria-label="Next month"
            >
                <ChevronRight className="h-4 w-4 mx-auto" />
            </button>
        </div>
    );
}

export function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
    return (
        <DayPicker
            showOutsideDays={showOutsideDays}
            className={cn("p-3", className)}
            classNames={{
                months: "flex flex-col space-y-4",
                month: "space-y-4",

                // (권장) v9 키로 변경
                month_caption: "pt-1",
                caption_label: "hidden",

                // ✅ table -> month_grid (v9)
                month_grid: "w-full border-collapse table-fixed",

                // ✅ <tr>에 flex 금지 (7열 깨짐 원인)
                weekdays: "",
                week: "",

                // 각 칸 폭 고정(선택)
                weekday: "text-muted-foreground w-9 p-0 pb-2 text-center font-normal text-[0.8rem]",
                day: "p-0 text-center align-middle",
                day_button: cn(buttonVariants({ variant: "ghost" }), "h-9 w-9 p-0 font-normal"),

                today: "bg-accent text-accent-foreground",
                outside: "text-muted-foreground opacity-50",
                disabled: "text-muted-foreground opacity-50",

                range_start:
                    "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
                range_end:
                    "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
                range_middle: "bg-accent text-accent-foreground",

                hidden: "invisible",
                ...classNames,
            }}
            components={{
                Nav: () => <></>, // ✅ null 대신 빈 element 반환
                MonthCaption: CustomMonthCaption,
            }}
            {...props}
        />
    );
}

// 혹시 default import 쓰는 곳이 있으면 안전하게 유지
export default Calendar;